import { Fragment, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import {
  setPaymentMethod,
  selectPaymentMethod
} from 'state/payment-method/payment-method-slice';
import { setAccountField, selectDetails } from 'state/account/account-slice';
import { pushEvent } from 'state/analytics/analytics-slice';
import { useNavigation } from 'hooks/useNavigation';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import NsContent from 'components/common/ns-content/NsContent';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsCaveat } from 'components/common/ns-caveat/NsCaveat';
import {
  RELATIONSHIP_TO_PROPERTY,
  PAYMENT_METHODS,
  PAYMENT_NAMES
} from 'config/app-config';
import { NsAlert } from 'components/common/ns-alert/NsAlert';
import { NsCaveatLink } from 'components/common/ns-caveat-link/NsCaveatLink';
import { useDDSavingsAmount } from 'hooks/useDDSavingsAmount';

export const testId = 'newHomeTestId';

export const NewHome = () => {
  const dispatch = useDispatch();

  const account = useSelector(selectDetails, shallowEqual);
  const { relationshipToProperty } = account;

  const paymentMethod = useSelector(selectPaymentMethod, shallowEqual);

  const { eventTypes, next } = useNavigation();
  const { savingsAmount, priceCapDate } = useDDSavingsAmount();

  const handlePaymentMethodChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      dispatch(setPaymentMethod(value));
    },
    [dispatch]
  );

  const handleRelationshipToPropertyChange = useCallback(
    (event) => {
      const {
        target: { name, value }
      } = event;
      const analyticsEvent = {
        category: 'occupancy-status',
        journey: 'new-home',
        status: value
      };
      dispatch(setAccountField({ name, value }));
      dispatch(pushEvent(analyticsEvent));
    },
    [dispatch]
  );

  const handleGoNext = useCallback(() => {
    next({ eventType: eventTypes.NEXT });
  }, [next, eventTypes]);

  const tipDetail = (
    <>
      <p>
        <strong>Monthly Direct Debit</strong> is one of the easiest ways to pay.
        You can pay a monthly fixed amount, where we calculate the cost of your
        energy for the year ahead and divide this into equal payments.
      </p>
      <p>
        <strong>Pay when you get the bill</strong> allows you to pay a variable
        amount every quarter, depending on the energy you use. You can pay with
        a credit or debit card online, by phone, by cheque or at the post
        office.
      </p>
    </>
  );

  return (
    <Fragment>
      <NsPanel testId={testId}>
        <NsBackCta />
        <NsContent>
          <NsTitle title="Setting up your new home" />
        </NsContent>
        <NsForm onSubmit={handleGoNext}>
          <NsRadioInput
            className="inline"
            heading={`What's your relationship to the property?`}
            name="relationshipToProperty"
            helper="If you rent the property please select tenant."
            onChange={handleRelationshipToPropertyChange}
            value={relationshipToProperty}
            options={[
              {
                value: RELATIONSHIP_TO_PROPERTY.TENANT,
                label: RELATIONSHIP_TO_PROPERTY.TENANT
              },
              {
                value: RELATIONSHIP_TO_PROPERTY.HOME_OWNER,
                label: RELATIONSHIP_TO_PROPERTY.HOME_OWNER
              }
            ]}
          />
          <NsRadioInput
            className="inline"
            heading={`How would you like to pay for your energy?`}
            name="paymentMethod"
            helper="What's the difference?"
            onChange={handlePaymentMethodChange}
            value={paymentMethod}
            options={[
              {
                value: PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
                label: PAYMENT_NAMES[PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT]
              },
              {
                value: PAYMENT_METHODS.CHECK,
                label: PAYMENT_NAMES[PAYMENT_METHODS.CHECK]
              }
            ]}
            tipDetail={tipDetail}
          />
          <NsAlert state="info">
            <h2 slot="heading">Save money with a Direct Debit</h2>
            <p>
              You could save <strong>{savingsAmount} per year</strong> on your
              bill when you pay by Direct Debit, compared to paying by card,
              cash or cheque every three months.
              <NsCaveatLink>
                Direct Debit saving based on a dual fuel customer with average
                consumption of 11,500 kWh for gas and 2,700 kWh for single-rate
                electricity at {priceCapDate} price cap rates.
              </NsCaveatLink>
            </p>
          </NsAlert>
          <NsPrimaryCta>Continue</NsPrimaryCta>
        </NsForm>
      </NsPanel>
      <NsCaveat heading="Additional information" />
    </Fragment>
  );
};

export default NewHome;
